import type { Warning } from '@/components/common/WarningIcon'
import type { VersionedWarnings } from '@/helpers/warningTypes'
import type { VaultListItem } from '@/lib/procedures/vaultList.contract'

import { getVaultWarningMessage, getVaultWarningTypeMessage } from '@/helpers/warningMessages'
import { WarningLevel } from '@/types/enums'

import { apiWarningLevelToEnum, getOwnApiVaultWarnings } from './warnings'

/**
 * Aggregates a vault's own warnings for list surfaces. Consumer-API rows are
 * pre-aggregated across sources, so keep only the ones matching the vault's own
 * version — this mirrors the detail-page aggregators, which never surface
 * market or underlying-adapter warnings on a vault.
 *
 * `warningType` is deliberately omitted: it makes hover-card rows link to a
 * section id that only exists on the vault page.
 * @internal
 */
export function aggregateVaultListWarnings(vault: VersionedWarnings): Warning[] {
  const warnings: Warning[] = []

  for (const warning of getOwnApiVaultWarnings(vault)) {
    const message = getVaultWarningMessage(warning)
    if (message) {
      warnings.push({ level: apiWarningLevelToEnum(warning.level), message })
    }
  }

  return warnings
}

const LIST_ITEM_LEVELS: Partial<Record<string, Warning['level']>> = {
  red: WarningLevel.Red,
  yellow: WarningLevel.Yellow
}

/** {@link aggregateVaultListWarnings} for a `vault.list` row, whose warnings are the vault's own. */
export function aggregateVaultListItemWarnings(warnings: VaultListItem['warnings']): Warning[] {
  return warnings.flatMap(({ type, level, content, delisted }) => {
    const warningLevel = LIST_ITEM_LEVELS[level]
    const message = getVaultWarningTypeMessage(type, { delisted, content })
    return warningLevel && message ? [{ level: warningLevel, message }] : []
  })
}
